import { useEffect, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { TopNav } from '@/app/components/navigation/top-nav';
import { ModuleSidebar } from '@/app/components/navigation/module-sidebar';
import { MobileNav } from '@/app/components/navigation/mobile-nav';
import { useRealtimeNotifications } from '@/hooks/use-realtime-notifications';
import { WsStatusProvider } from '@/contexts/ws-status-context';
import { OfflineBanner } from '@/app/components/offline/offline-banner';
import { ConflictLog } from '@/app/components/offline/conflict-log';
import { useOfflineStatus } from '@/hooks/use-offline';
import { useSafetyDocPrecache } from '@/hooks/use-safety-precache';
import { useSubscription } from '@/hooks/use-billing';
import { usePermissions } from '@/hooks/use-permissions';

function AdminAssetLimitBanner() {
  const { data, refetch } = useSubscription();
  const [now, setNow] = useState(() => Date.now());
  const state = data?.subscription?.asset_limit;
  const deadlineTime = state?.grace_deadline_at
    ? new Date(state.grace_deadline_at).getTime()
    : null;
  const isCreationBlocked = Boolean(
    state?.is_creation_blocked
    || (deadlineTime !== null && now >= deadlineTime)
  );

  useEffect(() => {
    if (deadlineTime === null || state?.is_creation_blocked) return;
    const interval = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(interval);
  }, [deadlineTime, state?.is_creation_blocked]);

  useEffect(() => {
    if (
      deadlineTime !== null
      && now >= deadlineTime
      && !state?.is_creation_blocked
    ) {
      void refetch();
    }
  }, [deadlineTime, now, refetch, state?.is_creation_blocked]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      void refetch();
    }, 60_000);
    return () => window.clearInterval(interval);
  }, [refetch]);

  if (
    !state
    || state.limit === null
    || (
      !state.is_over_limit
      && !state.is_creation_blocked
      && !state.recovery_started_at
    )
  ) return null;

  const excess = Math.max(state.current - state.limit, 0);
  const deadline = state.grace_deadline
    ? new Date(`${state.grace_deadline}T00:00:00`).toLocaleDateString()
    : null;
  const recoveryDeadline = state.recovery_deadline
    ? new Date(`${state.recovery_deadline}T00:00:00`).toLocaleDateString()
    : null;

  return (
    <div className="border-b border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2">
        <p>
          You have {state.current.toLocaleString()} assets on a plan for{' '}
          {state.limit.toLocaleString()}
          {state.is_over_limit && ` (${excess.toLocaleString()} over)`}.{' '}
          {state.recovery_started_at && isCreationBlocked
            ? (
                `New counted assets remain blocked during recovery. Stay at or `
                + `under the cap through ${recoveryDeadline}.`
              )
            : state.recovery_started_at
              ? `Stay at or under the cap through ${recoveryDeadline} to reset the grace period.`
            : isCreationBlocked
              ? 'New asset creation is blocked until you delete assets or upgrade.'
              : `Delete assets or upgrade by ${deadline}.`}
        </p>
        <Link className="font-medium underline" to="/settings/subscription">
          View upgrade options
        </Link>
      </div>
    </div>
  );
}

function AssetLimitBanner() {
  const { isAdmin } = usePermissions();
  return isAdmin ? <AdminAssetLimitBanner /> : null;
}

export function AppLayout() {
  // Establish WebSocket connection for real-time notifications
  const { wsConnected } = useRealtimeNotifications();

  const { isBannerVisible, dismissBanner, showConflictLog, setShowConflictLog, dismissConflicts } = useOfflineStatus();
  useSafetyDocPrecache();

  return (
    <WsStatusProvider wsConnected={wsConnected}>
      <div className={`min-h-screen bg-gray-50 dark:bg-background flex flex-col ${isBannerVisible ? 'pt-10' : ''}`}>
        {/* Offline Banner */}
        <OfflineBanner visible={isBannerVisible} onDismiss={dismissBanner} />

        {/* Conflict Log Modal */}
        <ConflictLog
          visible={showConflictLog}
          onClose={() => {
            setShowConflictLog(false);
            dismissConflicts();
          }}
        />

        {/* Top Navigation Bar */}
        <TopNav />
        <AssetLimitBanner />

        {/* Content area: sidebar + main */}
        <div className="flex flex-1">
          {/* Module Sidebar (desktop) */}
          <ModuleSidebar />

          {/* Page Content */}
          <main className="flex-1 pb-20 lg:pb-8 overflow-x-hidden">
            <Outlet />
          </main>
        </div>

        {/* Mobile Bottom Nav */}
        <div className="lg:hidden">
          <MobileNav />
        </div>
      </div>
    </WsStatusProvider>
  );
}
